import { HighlightAlt, RemoveCircleOutline } from "@mui/icons-material";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditIcon } from "../../../../../assets/images/icons/EditIcon";
import PlusIcon from "../../../../../assets/images/icons/PlusIcon";
import { useBriaTheme } from "../../../../../hooks/useBriaTheme";
import { usePopoverPosition } from "../hooks/usePopoverPosition";
import styles from "../StructuredPromptImageCanvas.module.scss";
import { CanvasObject } from "../types";
import { hexToRgb, rgbToHex } from "../utils/colorUtils";
import FiboColorPicker from "./FiboColorPicker";
import { KeypointsTabContent } from "./KeypointsTabContent";

const PANEL_WIDTH = 220;
const ROW_HEIGHT = 28;
const PANEL_OVERHEAD = 88;

export type TabValue = "colors" | "keypoints";

interface ObjectInspectorPanelProps {
	object: CanvasObject;
	bbox: { left: number; top: number; width: number; height: number };
	canvasSize: { width: number; height: number };
	onColorChange: (objectId: string, colorIndex: number, color: { r: number; g: number; b: number }) => void;
	onAddColor: (objectId: string) => void;
	onRemoveColor: (objectId: string, colorIndex: number) => void;
	/** Lifted to parent so ObjectKeypoints on the canvas can react to hover. */
	hoveredKeypointName: string | null;
	onKeypointHover: (name: string | null) => void;
	/** Controlled active tab — single source of truth shared with the canvas. */
	activeTab: TabValue;
	onTabChange: (tab: TabValue) => void;
	/** Turns this object's box into an embed area (see `seedEmbedArea`). */
	onEmbedHere: () => void;
}

export const ObjectInspectorPanel = observer(
	({
		object,
		bbox,
		canvasSize,
		onColorChange,
		onAddColor,
		onRemoveColor,
		hoveredKeypointName,
		onKeypointHover,
		activeTab,
		onTabChange,
		onEmbedHere,
	}: ObjectInspectorPanelProps) => {
		const { t } = useTranslation("fibo", { keyPrefix: "lab.canvas.inspector" });
		const { t: tCanvas } = useTranslation("fibo", { keyPrefix: "lab.canvas" });
		const [isEditMode, setIsEditMode] = useState(false);
		const [newColorIndex, setNewColorIndex] = useState<number | null>(null);
		const { getCssVariableValue } = useBriaTheme();

		const keypoints = object.keypoints ?? [];
		// Keypoints only exist for body/person objects; hide the tab otherwise.
		const hasKeypoints = keypoints.length > 0;

		// If the active tab became unavailable (object without keypoints), fall back to Colors.
		useEffect(() => {
			if (activeTab === "keypoints" && !hasKeypoints) {
				onTabChange("colors");
			}
		}, [activeTab, hasKeypoints, onTabChange]);

		const handleTabChange = (_: React.SyntheticEvent, val: TabValue) => {
			onTabChange(val);
		};

		const getContentHeight = () => {
			if (activeTab === "keypoints") return Math.min(keypoints.length, 6) * ROW_HEIGHT;
			return object.colors.length * ROW_HEIGHT;
		};
		const estimatedHeight = getContentHeight() + PANEL_OVERHEAD;
		const { left, top } = usePopoverPosition({ bbox, canvasSize, panelWidth: PANEL_WIDTH, estimatedHeight });

		return (
			<Box
				className={clsx(styles.colorPickerContainer, styles.objectInspectorPanel)}
				data-color-picker
				style={{ left: `${left}px`, top: `${top}px`, width: PANEL_WIDTH }}
			>
				{/* ── Tab bar ── */}
				<Box className={styles.inspectorTabBar}>
					<Tabs
						value={activeTab === "keypoints" && !hasKeypoints ? "colors" : activeTab}
						onChange={handleTabChange}
						className={styles.inspectorTabs}
					>
						<Tab label={t("colorsTab")} value="colors" />
						{hasKeypoints && <Tab label={t("keypointsTab")} value="keypoints" />}
					</Tabs>

					{/* Handoff to the canvas-wide embed area — deliberately NOT a tab, since it
				    doesn't edit the object, it hands its box over. Sits opposite the tabs
				    because `.inspectorTabs` takes the remaining space; no auto-margin, which
				    would land on a wrapper element and do nothing. */}
					<Box className={styles.inspectorEmbedButton} onClick={onEmbedHere}>
						<HighlightAlt className={styles.inspectorEmbedIcon} />
						<Typography className={styles.inspectorEmbedText}>{tCanvas("embedHere")}</Typography>
					</Box>
				</Box>

				{/* ── Colors tab ── */}
				{activeTab === "colors" && (
					<>
						<Box className={styles.colorPickerHeader} sx={{ mb: 0.5 }}>
							<Box
								className={styles.colorPickerEditIcon}
								onClick={() => setIsEditMode((prev) => !prev)}
								sx={{ ml: "auto" }}
							>
								<EditIcon className={styles.colorPickerEditIcon} />
							</Box>
						</Box>

						{object.colors.map((color, index) => {
							const hexColor = rgbToHex(color.r, color.g, color.b);
							return (
								<Box
									key={index}
									className={clsx(styles.colorPickerRow, isEditMode && styles.colorPickerRowEditMode)}
								>
									{isEditMode && (
										<Box
											className={styles.colorRemoveIcon}
											onClick={() => onRemoveColor(object.id, index)}
										>
											<RemoveCircleOutline sx={{ fontSize: 14 }} />
										</Box>
									)}
									<FiboColorPicker
										mode="hex"
										color={hexColor}
										autoOpen={index === newColorIndex}
										onChange={(newHex) => {
											setNewColorIndex(null);
											const newRgb = hexToRgb(newHex);
											if (newRgb) onColorChange(object.id, index, newRgb);
										}}
									/>
								</Box>
							);
						})}

						<Box
							className={styles.colorAddIcon}
							onClick={() => {
								setNewColorIndex(object.colors.length);
								onAddColor(object.id);
							}}
						>
							<PlusIcon fillColor={getCssVariableValue("--black-color")} height="8" />
						</Box>
					</>
				)}

				{/* ── Keypoints tab ── */}
				{activeTab === "keypoints" && keypoints.length > 0 && (
					<KeypointsTabContent
						keypoints={keypoints}
						hoveredName={hoveredKeypointName}
						onHover={onKeypointHover}
					/>
				)}
			</Box>
		);
	},
);
